import { persistQueryClient } from '@tanstack/query-persist-client-core';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import {
  defaultShouldDehydrateQuery,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React from 'react';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - prevent immediate refetching
      gcTime: 10 * 60 * 1000, // 10 minutes garbage collection
      retry: 2, // Reduced from 3 to speed up error handling
      refetchOnWindowFocus: false, // Disable to prevent excessive refetching
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 1,
    },
  },
});

const persister = createAsyncStoragePersister({
  storage: window.localStorage,
});

// Query roots that must never be restored from localStorage:
// - siteMessage: a stale outage banner must not reappear on reload
// - grantees/analyses/dashboard: mutated by archive/delete flows; rehydrated
//   copies land inside staleTime and are treated as fresh, so the UI keeps
//   showing pre-mutation data (e.g. a deleted grantee still "Active")
const NON_PERSISTED_QUERY_ROOTS = [
  'siteMessage',
  'grantees',
  'analyses',
  'dashboard',
];

export const isPersistableQueryKey = (queryKey: readonly unknown[]) =>
  !NON_PERSISTED_QUERY_ROOTS.includes(queryKey[0] as string);

persistQueryClient({
  queryClient,
  persister,
  maxAge: 60 * 60 * 1000, // 1 hour
  dehydrateOptions: {
    shouldDehydrateQuery: (query) =>
      isPersistableQueryKey(query.queryKey) &&
      defaultShouldDehydrateQuery(query),
  },
});

function QueryProvider({ children }: { children: React.ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default QueryProvider;
